import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">分类不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 做爱指南`,
    description: currentCategory.description,
    url: `https://relationx.github.io/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://relationx.github.io/article/${article.slug}`,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              {/* Breadcrumb */}
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    { label: currentCategory.name },
                  ]}
                />
              </div>

              {/* Category Header */}
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${currentCategory.icon} text-xl`}></i>
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                    {currentCategory.name}
                  </h1>
                </div>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                  {currentCategory.description}
                </p>
              </div>

              {/* Category Tabs */}
              <div className="flex flex-wrap items-center gap-2 mb-8 pb-4 border-b border-background-200/70">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Article List */}
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类下暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}